// src/pages/Register.jsx - Apartment Management System
import React, { useState, useEffect } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RiLockLine, RiUserLine, RiBuildingLine, RiLoginBoxLine, RiErrorWarningLine, RiMailLine, RiSmartphoneLine, RiCommunityLine, RiShieldKeyholeLine } from 'react-icons/ri';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { registerUser, verifyOTP } from '../models/authModel';
import Navbar from './home/Navbar';

const Register = () => {
    const { t } = useTranslation();
    const { isDarkMode } = useTheme();
    const { login } = useAuth();
    const navigate = useNavigate();

    const [step, setStep] = useState('register'); // 'register' or 'otp'
    const [userId, setUserId] = useState(null);
    const [otp, setOtp] = useState('');

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        username: '',
        email: '',
        phone: '',
        companyName: '',
        password: '',
        confirmPassword: ''
    });

    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [registerError, setRegisterError] = useState('');
    const [defaultCountry, setDefaultCountry] = useState('iq'); // Fallback location

    useEffect(() => {
        // Fetch location for default country code
        fetch('https://ipapi.co/json/')
            .then(res => res.json())
            .then(data => {
                if (data.country_code) setDefaultCountry(data.country_code.toLowerCase());
            })
            .catch(() => console.log('Location detection failed'));
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });

        // Clear errors when typing
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: ''
            });
        }

        // Clear register error when typing
        if (registerError) {
            setRegisterError('');
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.firstName.trim()) newErrors.firstName = t('first_name_required') || 'First name is required';
        if (!formData.lastName.trim()) newErrors.lastName = t('last_name_required') || 'Last name is required';
        if (!formData.username.trim()) newErrors.username = t('username_required') || 'Username is required';
        if (!formData.email.trim()) newErrors.email = t('email_required') || 'Email is required';
        if (!formData.phone || String(formData.phone).trim().length < 7) newErrors.phone = 'Phone number is required for WhatsApp verification';
        if (!formData.companyName.trim()) newErrors.companyName = t('company_name_required') || 'Company name is required';
        if (!formData.password) newErrors.password = t('password_required') || 'Password is required';
        if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = t('passwords_do_not_match') || 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        setRegisterError('');

        try {
            const response = await registerUser({
                firstName: formData.firstName,
                lastName: formData.lastName,
                username: formData.username,
                email: formData.email,
                phone: formData.phone,
                companyName: formData.companyName,
                password: formData.password
            });

            if (response.success) {
                if (response.requireOtp) {
                    setStep('otp');
                    setUserId(response.userId);
                    setRegisterError('');
                } else {
                    // Fallback for immediate login (if configured that way)
                    login(response.user, response.token);
                    navigate('/admin/dashboard');
                }
            } else {
                setRegisterError(response.message || t('registration_failed') || 'Registration failed');
            }
        } catch (error) {
            console.error('Registration error:', error);
            setRegisterError(error.message || t('registration_failed') || 'Registration failed');
        } finally {
            setIsLoading(false);
        }
    };

    const handleOtpSubmit = async (e) => {
        e.preventDefault();

        if (!otp || otp.length < 6) {
            setRegisterError('Please enter a valid verification code');
            return;
        }

        setIsLoading(true);
        setRegisterError('');

        try {
            const response = await verifyOTP(userId, otp);

            if (response.success) {
                login(response.user, response.token);
                navigate('/admin/dashboard');
            } else {
                setRegisterError(response.message || 'Verification failed');
            }
        } catch (error) {
            console.error('OTP error:', error);
            setRegisterError(error.message || 'Verification failed');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={`min-h-screen flex items-center justify-center p-4 pt-24 ${isDarkMode ? 'bg-zinc-950' : 'bg-white'}`}>
            <Navbar />
            <div className={`max-w-2xl w-full relative z-10 ${isDarkMode ? 'bg-zinc-900 border-white/[0.06]' : 'bg-white border-black/[0.1]'} border rounded-xl shadow-2xl overflow-hidden transition-all duration-300`}>
                <div className="p-6 sm:p-8">
                    <div className="text-center mb-8">
                        <div className="mb-4">
                            <img
                                src={isDarkMode ? "/quadcores-dark.png" : "/quadcores-light.png"}
                                alt="QuadCores Logo"
                                className="h-16 w-auto mx-auto"
                            />
                        </div>
                        <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                            {step === 'otp' ? 'Verify Your WhatsApp' : 'Quadcores Properties'}
                        </h1>
                        <p className={`mt-2 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-800'} font-light`}>
                            {step === 'otp'
                                ? 'A 6-digit verification code was sent to your WhatsApp. Please enter it below.'
                                : (t('sign_up_to_get_started') || 'Sign up to get started with your property estate')}
                        </p>
                    </div>

                    {registerError && (
                        <div className={`mb-6 p-3 rounded-lg flex items-center ${isDarkMode ? 'bg-red-900 bg-opacity-20 text-red-400 border border-red-800' : 'bg-zinc-100 text-red-700'
                            }`}>
                            <RiErrorWarningLine className="mr-2 flex-shrink-0" />
                            <span>{registerError}</span>
                        </div>
                    )}

                    {step === 'register' ? (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* First Name */}
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-zinc-800'}`}>
                                        {t('first_name') || 'First Name'}
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <RiUserLine className={isDarkMode ? 'text-gray-500' : 'text-zinc-400'} />
                                        </div>
                                        <input
                                            type="text"
                                            name="firstName"
                                            value={formData.firstName}
                                            onChange={handleChange}
                                            className={`block w-full pl-10 pr-3 py-3 ${isDarkMode
                                                ? 'bg-zinc-800 border-white/[0.1] text-white focus:border-red-500'
                                                : 'bg-zinc-50 border-black/[0.1] text-zinc-900 focus:border-red-500'
                                                } border rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500 ${errors.firstName ? 'border-red-500' : ''}`}
                                            placeholder={t('enter_first_name') || 'Enter first name'}
                                        />
                                    </div>
                                    {errors.firstName && <p className="mt-1 text-sm text-red-500">{errors.firstName}</p>}
                                </div>

                                {/* Last Name */}
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-zinc-800'}`}>
                                        {t('last_name') || 'Last Name'}
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <RiUserLine className={isDarkMode ? 'text-gray-500' : 'text-zinc-400'} />
                                        </div>
                                        <input
                                            type="text"
                                            name="lastName"
                                            value={formData.lastName}
                                            onChange={handleChange}
                                            className={`block w-full pl-10 pr-3 py-3 ${isDarkMode
                                                ? 'bg-zinc-800 border-white/[0.1] text-white focus:border-red-500'
                                                : 'bg-zinc-50 border-black/[0.1] text-zinc-900 focus:border-red-500'
                                                } border rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500 ${errors.lastName ? 'border-red-500' : ''}`}
                                            placeholder={t('enter_last_name') || 'Enter last name'}
                                        />
                                    </div>
                                    {errors.lastName && <p className="mt-1 text-sm text-red-500">{errors.lastName}</p>}
                                </div>

                                {/* Username */}
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-zinc-800'}`}>
                                        {t('username') || 'Username'}
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <RiUserLine className={isDarkMode ? 'text-gray-500' : 'text-zinc-400'} />
                                        </div>
                                        <input
                                            type="text"
                                            name="username"
                                            value={formData.username}
                                            onChange={handleChange}
                                            className={`block w-full pl-10 pr-3 py-3 ${isDarkMode
                                                ? 'bg-zinc-800 border-white/[0.1] text-white focus:border-red-500'
                                                : 'bg-zinc-50 border-black/[0.1] text-zinc-900 focus:border-red-500'
                                                } border rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500 ${errors.username ? 'border-red-500' : ''}`}
                                            placeholder={t('enter_username') || 'Enter username'}
                                        />
                                    </div>
                                    {errors.username && <p className="mt-1 text-sm text-red-500">{errors.username}</p>}
                                </div>

                                {/* Email */}
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-zinc-800'}`}>
                                        {t('email') || 'Email'}
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <RiMailLine className={isDarkMode ? 'text-gray-500' : 'text-zinc-400'} />
                                        </div>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            className={`block w-full pl-10 pr-3 py-3 ${isDarkMode
                                                ? 'bg-zinc-800 border-white/[0.1] text-white focus:border-red-500'
                                                : 'bg-zinc-50 border-black/[0.1] text-zinc-900 focus:border-red-500'
                                                } border rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500 ${errors.email ? 'border-red-500' : ''}`}
                                            placeholder={t('enter_email') || 'Enter email'}
                                        />
                                    </div>
                                    {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
                                </div>

                                {/* Phone */}
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-zinc-800'}`}>
                                        {t('phone') || 'Phone'}
                                    </label>
                                    <div dir="ltr">
                                        <PhoneInput
                                            country={defaultCountry}
                                            value={formData.phone}
                                            onChange={phone => handleChange({ target: { name: 'phone', value: '+' + phone } })}
                                            enableSearch={true}
                                            disableSearchIcon={true}
                                            buttonStyle={{
                                                backgroundColor: isDarkMode ? '#27272a' : '#f9fafb',
                                                borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                                            }}
                                            inputStyle={{
                                                width: '100%',
                                                backgroundColor: isDarkMode ? '#27272a' : '#f9fafb',
                                                borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                                                color: isDarkMode ? '#ffffff' : '#000000',
                                                paddingTop: '23px', paddingBottom: '23px', borderRadius: '8px'
                                            }}
                                            dropdownStyle={{
                                                backgroundColor: isDarkMode ? '#18181b' : '#ffffff',
                                                color: isDarkMode ? '#ffffff' : '#000000',
                                            }}
                                        />
                                    </div>
                                    {errors.phone && <p className="mt-1 text-sm text-red-500">{errors.phone}</p>}
                                </div>

                                {/* Company Name */}
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-zinc-800'}`}>
                                        {t('company_name') || 'Company Name'}
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <RiCommunityLine className={isDarkMode ? 'text-gray-500' : 'text-zinc-400'} />
                                        </div>
                                        <input
                                            type="text"
                                            name="companyName"
                                            value={formData.companyName}
                                            onChange={handleChange}
                                            className={`block w-full pl-10 pr-3 py-3 ${isDarkMode
                                                ? 'bg-zinc-800 border-white/[0.1] text-white focus:border-red-500'
                                                : 'bg-zinc-50 border-black/[0.1] text-zinc-900 focus:border-red-500'
                                                } border rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500 ${errors.companyName ? 'border-red-500' : ''}`}
                                            placeholder={t('enter_company_name') || 'Enter company name'}
                                        />
                                    </div>
                                    {errors.companyName && <p className="mt-1 text-sm text-red-500">{errors.companyName}</p>}
                                </div>

                                {/* Password */}
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-zinc-800'}`}>
                                        {t('password') || 'Password'}
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <RiLockLine className={isDarkMode ? 'text-gray-500' : 'text-zinc-400'} />
                                        </div>
                                        <input
                                            type="password"
                                            name="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            className={`block w-full pl-10 pr-3 py-3 ${isDarkMode
                                                ? 'bg-zinc-800 border-white/[0.1] text-white focus:border-red-500'
                                                : 'bg-zinc-50 border-black/[0.1] text-zinc-900 focus:border-red-500'
                                                } border rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500 ${errors.password ? 'border-red-500' : ''}`}
                                            placeholder={t('enter_password') || 'Enter password'}
                                        />
                                    </div>
                                    {errors.password && <p className="mt-1 text-sm text-red-500">{errors.password}</p>}
                                </div>

                                {/* Confirm Password */}
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-zinc-800'}`}>
                                        {t('confirm_password') || 'Confirm Password'}
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <RiLockLine className={isDarkMode ? 'text-gray-500' : 'text-zinc-400'} />
                                        </div>
                                        <input
                                            type="password"
                                            name="confirmPassword"
                                            value={formData.confirmPassword}
                                            onChange={handleChange}
                                            className={`block w-full pl-10 pr-3 py-3 ${isDarkMode
                                                ? 'bg-zinc-800 border-white/[0.1] text-white focus:border-red-500'
                                                : 'bg-zinc-50 border-black/[0.1] text-zinc-900 focus:border-red-500'
                                                } border rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500 ${errors.confirmPassword ? 'border-red-500' : ''}`}
                                            placeholder={t('confirm_password') || 'Confirm password'}
                                        />
                                    </div>
                                    {errors.confirmPassword && <p className="mt-1 text-sm text-red-500">{errors.confirmPassword}</p>}
                                </div>
                            </div>

                            <div>
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className={`w-full py-3 px-4 ${isDarkMode
                                        ? 'bg-red-600 hover:bg-red-700'
                                        : 'bg-red-600 hover:bg-red-700'
                                        } text-white rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${isDarkMode ? 'focus:ring-offset-gray-900' : 'focus:ring-offset-white'
                                        } flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                                >
                                    {isLoading ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            {t('registering') || 'Creating Account'}...
                                        </>
                                    ) : (
                                        <>
                                            <RiLoginBoxLine className="mr-2" />
                                            {t('register') || 'Create Account'}
                                        </>
                                    )}
                                </button>
                            </div>

                            <div className="text-center mt-4">
                                <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-zinc-800'} font-light`}>
                                    {t('already_have_account') || 'Already have an account?'} {' '}
                                    <Link to="/login" className={`font-medium ${isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-700 hover:text-red-600'}`}>
                                        {t('login') || 'Sign In'}
                                    </Link>
                                </p>
                            </div>
                        </form>
                    ) : (
                        <form onSubmit={handleOtpSubmit} className="space-y-6">
                            <div>
                                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-zinc-800'}`}>
                                    Verification Code
                                </label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={otp}
                                        onChange={(e) => setOtp(e.target.value)}
                                        className={`block w-full px-3 py-3 text-center text-2xl tracking-widest ${isDarkMode
                                            ? 'bg-zinc-800 border-white/[0.1] text-white focus:border-red-500'
                                            : 'bg-zinc-50 border-black/[0.1] text-zinc-900 focus:border-red-500'
                                            } border rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500`}
                                        placeholder="123456"
                                        maxLength={6}
                                    />
                                </div>
                                <p className={`mt-2 text-sm text-center ${isDarkMode ? 'text-zinc-400' : 'text-zinc-800'} font-light`}>
                                    Please enter the 6-digit code sent to <strong>{formData.phone}</strong>
                                </p>
                            </div>

                            <div>
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className={`w-full py-3 px-4 ${isDarkMode
                                        ? 'bg-red-600 hover:bg-red-700'
                                        : 'bg-red-600 hover:bg-red-700'
                                        } text-white rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${isDarkMode ? 'focus:ring-offset-gray-900' : 'focus:ring-offset-white'
                                        } flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                                >
                                    {isLoading ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Verifying...
                                        </>
                                    ) : (
                                        <>
                                            <RiShieldKeyholeLine className="mr-2" />
                                            Verify Phone
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Register;
